import React from 'react';
import { Spinner } from '@apia/theme';
import { ContentContext } from '../../../../hooks/useContentContext';
import useContext from '../../../../hooks/useContext';
import useModal from '../../../../hooks/useModal';
import useTemplate from '../../../../templater/useTemplate';
import { TNoUndefinedField, TRequireOnlyOne } from '../../../../types';
import { arrayOrArray } from '../../../../utils/util';
import { Modal } from '../../../common';
import UploaderContext from '../../../common/uploader/context';
import {
  TDocumentDownloadHistory,
  TDocumentInformation,
  TUploaderFileInfo,
} from '../../../common/uploader/types';
import { FileElementContext } from '../../execution/fields/apiaUploader/FileElement';
import { OpenModalContext } from './OpenModalContext';

export type TDocumentInfoModal = {
  children: React.ReactNode;
  hidePermissions?: boolean;
  hideDownloadHistory?: boolean;
  fromMonitor?: boolean;
  className?: string;
} & TRequireOnlyOne<{
  file: TUploaderFileInfo;
  getFile: () => Promise<ReturnType<typeof parseDocumentInfo> | null>;
}>;

export function parseDocumentInfo(
  data: TDocumentInformation | null | undefined,
) {
  if (!data) throw new Error('There is no data in DocumentInfoModal');
  const { general } = data.function.data;
  return {
    allPoolPermission: general.docAllPoolPerm,
    description: general.docDesc,
    downloadLink: general.docDwnExternal,
    expirationDate: general.docExpDate ?? '',
    metadata: arrayOrArray(data.function.metadatas?.metadata),
    name: general.docName,
    permissions: data.function.data?.permissions,
    type: general.docTypeLabel,
    versions: arrayOrArray(data.function.data?.versions?.version),
    folderPath: data.function.data.general.docFolderPath,
  };
}

export type TDocumentsInfoModalContext = TNoUndefinedField<
  ReturnType<typeof parseDocumentInfo>
> & {
  file: TUploaderFileInfo;
  docDownHistory?: {
    docHistory: TDocumentDownloadHistory | TDocumentDownloadHistory[];
  };
  lock: string;
  folderPath: string;
};

const DocumentInfoModal = ({
  children,
  className,
  file,
  getFile,
  hidePermissions,
  hideDownloadHistory,
  fromMonitor,
}: TDocumentInfoModal) => {
  const [content, setContent] = React.useState<null | Record<string, unknown>>(
    null,
  );
  const { api } = useContext(UploaderContext);
  const { noDownload } = useContext(FileElementContext);

  const { openModalContextValue, ...modalProps } = useModal(
    React.useMemo(
      () => ({
        preFetcher() {
          void (async () => {
            let newContent: Record<string, unknown> | null = {};
            if (file) {
              newContent = {
                ...parseDocumentInfo(
                  await api.getDocumentInfo({ docId: file.docId }),
                ),
                file,
              };
            } else {
              newContent = await getFile();
            }
            if (newContent) setContent(newContent);
            else console.error('The retrieved data is not correct');
          })();

          return new Promise((resolve) => {
            resolve();
          });
        },
        onClose: () => {
          setContent(null);
        },
      }),
      [api, file, getFile],
    ),
  );
  const template = useTemplate('modals.documentInfo', {
    props: React.useMemo(() => {
      return {
        ...content,
        fromMonitor,
        showMetadata:
          (content?.metadata as unknown[])?.length > 0 &&
          (fromMonitor ? !hidePermissions : true),
        showHistory:
          (hidePermissions && hideDownloadHistory) ||
          (hidePermissions === undefined && hideDownloadHistory === undefined),
        showPermissions: fromMonitor ? !hidePermissions : true,
        showDownloadInfo: fromMonitor ? !hideDownloadHistory : false,
        disableDownload: noDownload,
      };
    }, [
      content,
      fromMonitor,
      hideDownloadHistory,
      hidePermissions,
      noDownload,
    ]),
    reparseOnPropsChange: true,
  });

  return (
    <OpenModalContext.Provider value={openModalContextValue}>
      {children}
      <Modal
        title={
          hidePermissions && hideDownloadHistory
            ? window.BTN_FILE_HISTORY_LBL
            : window.BTN_FILE_INFO_LBL
        }
        {...modalProps}
        className={`${className ?? ''} documentInfoModal`}
        variant="layout.common.modals.documentsInfo"
        size="flex"
      >
        {content === null ? (
          <Spinner />
        ) : (
          <ContentContext.Provider value={content}>
            {template}
          </ContentContext.Provider>
        )}
      </Modal>
    </OpenModalContext.Provider>
  );
};

export default React.memo(DocumentInfoModal);
